import { addBreadcrumb, captureException } from '@sentry/react';
import getConfig from 'next/config';

import logger from 'src/clients/LoggingClient';
import { SheriffTokenResponse, clearAuthState, getAuthState, setAuthState } from 'src/state/auth/auth';
import { getDeviceId } from 'src/utils/deviceId';

export enum Domain {
  RHPublic,
  RHCreditCard,
  RHCrossSell,
  RHVGS,
  RHIdenti,
}

const { publicRuntimeConfig } = getConfig();
const { API_ENV } = publicRuntimeConfig;

export const ApiEnv = API_ENV;

export const OauthScope = 'internal';

export enum OauthClientId {
  Production = 'c82SH0WZOsabOXGP2sxqcj34FxkvfnWRZBKlBjFS',
  Development = 'paOTfLGbfyouEUdZDqJ0fXQrNR3f9LWVjgRysM7p',
  Apollo = 'client_id',
}

export const getApiDomain = (domain: Domain) => {
  switch (domain) {
    case Domain.RHCreditCard:
      const baseX1ApiUrl =
        ApiEnv === 'production' ? 'https://api.robinhood.com' : 'https://api-staging.x1creditcard.com';
      return `${publicRuntimeConfig.API_DOMAIN ?? baseX1ApiUrl}/creditcard`;
    case Domain.RHCrossSell:
      const baseXsellApiUrl =
        ApiEnv === 'production' ? 'https://api.robinhood.com' : 'https://xsell-api.staging.internal.x1.co';
      return `${publicRuntimeConfig.XSELL_API_DOMAIN ?? baseXsellApiUrl}/banking/cross-sell`;
    case Domain.RHPublic:
      return ApiEnv === 'production' ? 'https://api.robinhood.com' : 'https://api--x1-apollo.apollo.rhinternal.net';
    case Domain.RHIdenti:
      return ApiEnv === 'production'
        ? 'https://identi.robinhood.com'
        : 'https://api--x1-apollo.apollo.rhinternal.net/identi';
  }
};

// NOTE (szhang): using a shared promise variable as a mutex to prevent multiple refreshes at one time
let TokenRefreshPromise: Promise<string | null> | undefined;

export const fetch = async (
  domain: Domain,
  path: string,
  init?: RequestInit,
  withCredentials: boolean = true,
): Promise<Response> => {
  const url = `${getApiDomain(domain)}${path}`;
  addBreadcrumb({
    type: 'HTTP Fetch',
    level: 'debug',
    data: {
      url,
    },
  });

  const authToken = withCredentials ? getAuthState()?.access_token : null;

  try {
    const r = await global.fetch(url, {
      ...init,
      headers: {
        ...init?.headers,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        // NOTE (szhang): temp commenting out to test CORS checks
        // 'X-X1-Client': 'web-onboarding-rh',
      },
    });
    if (!r.ok) logger.error(new Error(`HTTP Error ${r.status}: ${r.statusText}`), { source: 'fetch', url });
    if (withCredentials && (r.status === 401 || r.status === 403)) {
      if (TokenRefreshPromise == null) {
        TokenRefreshPromise = refreshToken().then((res) => {
          TokenRefreshPromise = undefined;
          return res;
        });
      }
      const res = await TokenRefreshPromise;
      if (res != null) {
        return fetch(domain, path, init, withCredentials);
      }
    }
    return r;
  } catch (e) {
    addBreadcrumb({ message: `Fetch error for ${url}`, category: 'error' });
    logger.error(e, { source: 'fetch', url });
    throw e;
  }
};

export const refreshToken = async (): Promise<string | null> => {
  try {
    const refresh_token = getAuthState()?.refresh_token;
    if (!!refresh_token) {
      const res = await fetch(
        Domain.RHPublic,
        `/oauth2/token/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            grant_type: 'refresh_token',
            scope: OauthScope,
            create_read_only_secondary_token: true,
            client_id: ApiEnv === 'production' ? OauthClientId.Production : OauthClientId.Apollo,
            refresh_token,
            device_token: getDeviceId(),
            token_request_path: '/creditcard/application',
            expires_in: 86400,
          }),
        },
        false,
      );
      if (res.ok) {
        const jsonRes = await res.json();
        if (
          jsonRes.hasOwnProperty('access_token') &&
          jsonRes.hasOwnProperty('refresh_token') &&
          jsonRes.hasOwnProperty('expires_in')
        ) {
          setAuthState(jsonRes as SheriffTokenResponse);
          return jsonRes.access_token;
        }
      } else if (res.status === 401 || res.status === 403) {
        logger.warn('Refresh token failed', { source: 'refreshToken', status: res.status });
        await clearAuthState();
        return null;
      }
    } else {
      logger.warn('Refresh token missing', { source: 'refreshToken' });
      await clearAuthState();
      return null;
    }
  } catch (e) {
    await clearAuthState();
    logger.error(e, { source: 'refreshToken' });
  }
  return null;
};
